import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { Chip, LanguagePill } from '../../components/shared/Badges';
import { Card, Kpi, KpiLedger } from '../../components/shared/Card';
import { Avatar } from '../../components/shared/People';
import { MenuItem, MenuTitle, Popover, usePopover } from '../../components/shared/Overlay';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { BarChart, HBars, nice, useTableToggle } from '../../components/shared/charts';
import { PRESENCE_CLASS, analyticsApi, hourClock, hourLabel, hourRange, useAnalytics } from '../../services/analyticsApi';

export { AgentDetailPage } from './AgentDetailPage';

const LANGUAGE_NAMES = { EN: 'English', ES: 'Spanish' };

function OptionChip({ icon, label, value, options, onChange }) {
  const pop = usePopover();
  const current = options.find((o) => o.value === value);
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      width={240}
      label={label}
      anchor={
        <Chip icon={icon} keyLabel={label} active={Boolean(value)} onClick={pop.toggle} aria-haspopup="true" aria-expanded={pop.open}>
          {current ? current.label : 'All'}
        </Chip>
      }
    >
      <MenuTitle>{label}</MenuTitle>
      <MenuItem
        checkable
        on={!value}
        onClick={() => {
          onChange(null);
          pop.close();
        }}
      >
        All
      </MenuItem>
      {options.map((o) => (
        <MenuItem
          key={o.value}
          checkable
          on={o.value === value}
          onClick={() => {
            onChange(o.value);
            pop.close();
          }}
        >
          {o.label}
        </MenuItem>
      ))}
    </Popover>
  );
}

const OPTIONAL_COLUMNS = [
  ['status', 'Status'],
  ['answerRate', 'Answer rate'],
  ['aht', 'Avg handle time'],
  ['transfers', 'Transfers'],
  ['sentiment', 'Caller sentiment'],
  ['languages', 'Languages'],
];

function ColumnsButton({ hidden, onToggle }) {
  const pop = usePopover();
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      align="right"
      width={220}
      label="Columns"
      anchor={
        <button className="btn btn-ghost btn-sm" type="button" onClick={pop.toggle} aria-haspopup="true" aria-expanded={pop.open}>
          <Icon name="columns" className="ico-sm" />
          Columns
        </button>
      }
    >
      <MenuTitle>Columns</MenuTitle>
      {OPTIONAL_COLUMNS.map(([key, name]) => (
        <MenuItem key={key} checkable on={!hidden.includes(key)} onClick={() => onToggle(key)}>
          {name}
        </MenuItem>
      ))}
    </Popover>
  );
}

function SentimentBar({ row }) {
  const neutral = Math.max(0, 100 - row.positivePercent - row.negativePercent);
  if (!row.analyzedCalls) return <span className="caption">No analyzed calls</span>;
  return (
    <>
      <div
        style={{ display: 'flex', gap: 2, height: 8, width: 120 }}
        title={`Positive ${row.positivePercent.toFixed(0)}%, neutral ${neutral.toFixed(0)}%, negative ${row.negativePercent.toFixed(0)}%`}
      >
        <span style={{ flex: row.negativePercent, background: 'var(--div-neg-2)', borderRadius: '2px 0 0 2px' }} />
        <span style={{ flex: neutral, background: 'var(--div-mid)' }} />
        <span style={{ flex: row.positivePercent, background: 'var(--div-pos-2)', borderRadius: '0 2px 2px 0' }} />
      </div>
      <div className="caption num">{row.positivePercent.toFixed(0)}% positive</div>
    </>
  );
}

function AgentTable({ data, rows, hidden, onOpen }) {
  const maxAht = Math.max(1, ...rows.map((r) => r.averageHandleSeconds));
  const show = (key) => !hidden.includes(key);
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Agent</th>
            {show('status') && <th>Status</th>}
            <th className="n">Handled</th>
            {show('answerRate') && <th className="n">Answer rate</th>}
            {show('aht') && <th>Avg handle time</th>}
            {show('transfers') && <th className="n">Transfers</th>}
            {show('sentiment') && <th>Caller sentiment</th>}
            {show('languages') && <th>Languages</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.agentId} className="clickable" tabIndex={0} onClick={() => onOpen(a)} onKeyDown={(e) => e.key === 'Enter' && onOpen(a)}>
              <td>
                <div className="row" style={{ gap: 10 }}>
                  <Avatar name={a.agentName} photoId={a.agentId} />
                  <div>
                    <div className="cell-title">{a.agentName}</div>
                    <div className="cell-sub">{a.primaryQueueName || 'No queue'}</div>
                  </div>
                </div>
              </td>
              {show('status') && (
                <td>
                  <span className="row small" style={{ gap: 6 }}>
                    <span className={`presence p-${PRESENCE_CLASS[a.presence.status] || 'offline'}`} />
                    {a.presence.label}
                  </span>
                </td>
              )}
              <td className="n">{fmt.int(a.answeredCalls)}</td>
              {show('answerRate') && (
                <td className="n">
                  {a.answerRate < 90 ? (
                    <span className="sev sev-48h">
                      <Icon name="alert" />
                      {a.answerRate.toFixed(1)}%
                    </span>
                  ) : (
                    `${a.answerRate.toFixed(1)}%`
                  )}
                </td>
              )}
              {show('aht') && (
                <td>
                  <div className="bar-cell">
                    <span className="num small" style={{ width: 36 }}>
                      {fmt.mmss(a.averageHandleSeconds)}
                    </span>
                    <div className="track">
                      <div className="fill" style={{ width: `${(a.averageHandleSeconds / maxAht) * 100}%` }} />
                    </div>
                  </div>
                </td>
              )}
              {show('transfers') && <td className="n">{fmt.int(a.transferredCalls)}</td>}
              {show('sentiment') && (
                <td>
                  <SentimentBar row={a} />
                </td>
              )}
              {show('languages') && (
                <td>
                  {a.languages.map((l, i) => (
                    <React.Fragment key={l}>
                      {i > 0 && ' '}
                      <LanguagePill code={l} title={LANGUAGE_NAMES[l] || l} />
                    </React.Fragment>
                  ))}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoverageCard({ data }) {
  const table = useTableToggle();
  const language = LANGUAGE_NAMES[data.coverageLanguage] || data.coverageLanguage;
  const byHour = Object.fromEntries(data.languageCoverage.map((c) => [c.hour, c]));
  const hours = hourRange(Array.from({ length: 24 }, (_, h) => byHour[h]?.calls || 0), 8, 17);
  const peak = data.languageCoverage.slice().sort((a, b) => b.callsPerAgent - a.callsPerAgent)[0];
  const agents = peak ? Math.max(1, Math.round(peak.agents)) : 0;
  return (
    <Card className="c-6" title={`${language}-language coverage`} description={`${language} calls per ${language}-speaking agent signed in, weekday average`} actions={table.button}>
      <BarChart
        labels={hours.map(hourLabel)}
        xName="Hour"
        height={210}
        yFmt={(v) => v.toFixed(0)}
        vFmt={(v) => `${Number(v).toFixed(1)} calls per agent`}
        target={{ value: 5, label: 'Comfortable load: 5 per hour' }}
        series={[{ name: `Calls per ${language}-speaking agent`, values: hours.map((h) => byHour[h]?.callsPerAgent || 0), slot: 1 }]}
        showTable={table.on}
      />
      <div className="caption mt-2">
        Language is detected from the recording, so calls without a recording aren't counted.
        {peak
          ? ` ${hourClock(peak.hour)} to ${hourClock(peak.hour + 1)} has ${agents} ${language}-speaking ${agents === 1 ? 'agent' : 'agents'} for an average of ${fmt.int(peak.calls)} ${peak.calls === 1 ? 'call' : 'calls'}.`
          : ` No ${language} calls in this period.`}
      </div>
    </Card>
  );
}

export function AgentsPage() {
  const h = useHierarchyFilter();
  const navigate = useNavigate();
  const [queueId, setQueueId] = useState(null);
  const [language, setLanguage] = useState(null);
  const [signedInOnly, setSignedInOnly] = useState(false);
  const [hidden, setHidden] = useState([]);
  const params = useMemo(() => ({ ...h.params, ...(queueId ? { queueIds: queueId } : {}), ...(language ? { languages: language.toLowerCase() } : {}) }), [h.params, queueId, language]);
  const { data, error, retry } = useAnalytics(() => analyticsApi.agents(params), JSON.stringify(params));
  const [queueOptions, setQueueOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);

  React.useEffect(() => {
    if (!data || queueId || language) return;
    const queues = new Map();
    const languages = new Set();
    data.agents.forEach((a) => {
      if (a.primaryQueueId) queues.set(a.primaryQueueId, a.primaryQueueName);
      a.languages.forEach((l) => languages.add(l));
    });
    setQueueOptions([...queues].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)));
    setLanguageOptions([...languages].sort().map((l) => ({ value: l, label: LANGUAGE_NAMES[l] || l })));
  }, [data, queueId, language]);

  const filters = (
    <>
      <OptionChip icon="layers" label="Queue" value={queueId} options={queueOptions} onChange={setQueueId} />
      <OptionChip icon="globe" label="Language" value={language} options={languageOptions} onChange={setLanguage} />
    </>
  );

  const rows = data ? data.agents.filter((a) => !signedInOnly || a.presence.status !== 'offline') : [];
  const exportColumns = [
    { key: 'agentName', header: 'Agent' },
    { key: 'primaryQueueName', header: 'Queue' },
    { key: 'status', header: 'Status', exportValue: (r) => r.presence.label },
    { key: 'answeredCalls', header: 'Handled' },
    { key: 'answerRate', header: 'Answer rate' },
    { key: 'averageHandleSeconds', header: 'Avg handle time', exportValue: (r) => fmt.mmss(r.averageHandleSeconds) },
    { key: 'transferredCalls', header: 'Transfers' },
    { key: 'positivePercent', header: 'Positive %' },
    { key: 'languages', header: 'Languages', exportValue: (r) => r.languages.join(' ') },
  ];

  let body;
  if (error) body = <ErrorState title="Agents couldn't load" body="Vertolytics couldn't reach the analytics service. Agent figures are unavailable until it responds." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={420} />;
  else if (!data.agents.length)
    body = (
      <EmptyState
        icon="headset"
        title="No agents answered calls in this period"
        body="Agents appear after they handle a call routed through a Teams call queue or auto attendant. Check the date range, or confirm queue membership in Teams admin center."
      />
    );
  else {
    const t = data.team;
    const average = Math.round(t.handledPerAgent);
    const top = data.agents.slice(0, 10);
    const max = nice(Math.max(average, ...top.map((a) => a.answeredCalls)) * 1.1, 4).max;
    body = (
      <div className="stack-lg">
        <KpiLedger>
          <Kpi label="Signed in now" value={t.signedInNow == null ? '—' : fmt.int(t.signedInNow)} meta={`of ${t.agents} agents`} />
          <Kpi label="Calls handled" value={fmt.int(t.handledCalls)} meta={`${fmt.int(t.handledPerAgent)} per agent`} />
          <Kpi label="Answer rate" value={`${t.answerRate.toFixed(1)}%`} meta="offered calls answered" />
          <Kpi label="Average handle time" value={fmt.mmss(t.averageHandleSeconds)} meta={`team median ${fmt.mmss(t.medianHandleSeconds)}`} />
          <Kpi label="Transfer rate" value={`${t.transferRate.toFixed(1)}%`} meta={`${fmt.int(t.transferredCalls)} transferred`} />
          <Kpi label="Positive sentiment" value={`${t.positivePercent.toFixed(1)}%`} meta="callers ending positive" />
        </KpiLedger>
        <div className="grid">
          <Card
            className="c-12"
            flush
            title="Agent performance"
            description={`${data.agents.length} agents, sorted by calls handled`}
            footer={null}
            actions={
              <>
                <div className="seg" role="group" aria-label="Show">
                  <button type="button" aria-pressed={!signedInOnly ? 'true' : 'false'} onClick={() => setSignedInOnly(false)}>
                    All agents
                  </button>
                  <button type="button" aria-pressed={signedInOnly ? 'true' : 'false'} onClick={() => setSignedInOnly(true)}>
                    Signed in now
                  </button>
                </div>
                <ColumnsButton hidden={hidden} onToggle={(key) => setHidden((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]))} />
              </>
            }
          >
            <AgentTable data={data} rows={rows} hidden={hidden} onOpen={(a) => navigate(`/agents/${encodeURIComponent(a.agentId)}`)} />
            <div className="table-foot">
              <span>Handle time = talk + hold, excluding wrap-up. Sentiment covers AI-analyzed calls only.</span>
            </div>
          </Card>
          <Card className="c-6" title="Calls handled" description={`Team average ${fmt.int(average)}, shown as a line on each bar`}>
            <HBars items={top.map((a) => ({ label: a.agentName, value: a.answeredCalls, marker: average }))} max={max} />
          </Card>
          <CoverageCard data={data} />
        </div>
      </div>
    );
  }

  return (
    <Page
      title="Agents"
      subtitle="Teams users who answered calls in the selected period. Click an agent for their detail."
      exportConfig={{ name: 'Agents', columns: exportColumns, rows }}
      filters={filters}
    >
      {body}
    </Page>
  );
}
